import { Clock } from "lucide-react"
import { Card, CardContent } from "@/modules/shared/ui/components/card"
import { Badge } from "@/modules/shared/ui/components/badge"
import { OffboardingStatus, isOffboardingStatus } from "@/modules/offboarding/enums"

interface Props {
  resignationStatus: string
  terminationEligibleAt: string | null
  gracePeriodDays: number | null
  createdAt: string
  updatedAt: string
}

const STATUS_CLASSES: Record<string, string> = {
  pending: "bg-yellow-100 text-yellow-800 border-yellow-200",
  accepted: "bg-green-100 text-green-700 border-green-200",
  rejected: "bg-red-100 text-red-700 border-red-200",
}

const MS_PER_DAY = 24 * 60 * 60 * 1000

export function OffboardingStatusCard({ resignationStatus, terminationEligibleAt, gracePeriodDays, createdAt, updatedAt }: Props) {
  const statusCls = STATUS_CLASSES[resignationStatus.toLowerCase()] ?? "bg-muted text-foreground border-border"
  const isAccepted = isOffboardingStatus(resignationStatus, OffboardingStatus.Accepted)
  const terminationDate = terminationEligibleAt ? new Date(terminationEligibleAt) : null
  const daysRemaining = terminationDate
    ? Math.ceil((terminationDate.getTime() - Date.now()) / MS_PER_DAY)
    : null
  const isTerminated = daysRemaining !== null && daysRemaining <= 0

  return (
    <Card className="bg-card border border-border">
      <CardContent className="p-6">
        <h2 className="text-base font-semibold text-foreground mb-4">Resignation Status</h2>
        <Badge className={statusCls}>{resignationStatus}</Badge>
        {isAccepted && terminationDate && (
          <div className={`mt-3 flex items-start gap-2 rounded-md border p-3 text-sm ${isTerminated ? "border-red-200 bg-red-50 text-red-700" : "border-amber-200 bg-amber-50 text-amber-800"}`}>
            <Clock className="w-4 h-4 mt-0.5 shrink-0" />
            <span>
              {isTerminated ? (
                `Access terminated on ${terminationDate.toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" })}`
              ) : (
                <>
                  <span className="font-semibold">
                    {daysRemaining} day{daysRemaining === 1 ? "" : "s"} left on the platform
                  </span>
                  <span className="block text-xs opacity-75 mt-0.5">
                    Access ends {terminationDate.toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" })}
                    {gracePeriodDays != null ? ` (${gracePeriodDays}-day grace period)` : ""}
                  </span>
                </>
              )}
            </span>
          </div>
        )}
        <div className="mt-4 pt-4 border-t border-border space-y-2 text-sm text-muted-foreground">
          <p>Created: {new Date(createdAt).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}</p>
          <p>Updated: {new Date(updatedAt).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}</p>
        </div>
      </CardContent>
    </Card>
  )
}
